/**
 * Workspace Registry (Sprint 6 · LAW-007).
 *
 * Presentation authority for Coolman Operations navigation. Sidebar,
 * mobile nav, breadcrumbs, and global search are ALL derived from this
 * file. Component metadata (permissions, versioning, lifecycle) still
 * lives in `/app/memory/architecture/COMPONENT_REGISTRY.md`.
 *
 * Adding a component to a new workspace, changing sidebar order, or
 * regrouping is a one-line edit here — no menu code changes, no
 * permission migrations, no API renames (per LAW-007).
 */
import {
  Compass, Gauge, SunHorizon, ClipboardText, MapPin,
  Package, CurrencyDollar, ShoppingCart, GasPump, Stack,
  Users, Truck, CalendarCheck, UsersThree,
  Phone, FileText, ChatCircleDots,
  Gear, Sliders, Sun, Plus, GearSix, EnvelopeSimple,
} from "@phosphor-icons/react";


/**
 * Quick Actions — global create-shortcuts. These are ACTIONS, not
 * destinations, so they live outside the workspace hierarchy and
 * render in their own section at the top of every nav surface.
 * (ARC-012 · presentation-only; no permission gating beyond `roles`.)
 */
export const QUICK_ACTIONS = [
  {
    to: "/new-order",
    label: "New Order",
    icon: Plus,
    comp: "COMP-011",
    testid: "quick-new-order",
    roles: ["Administrator", "Office Staff", "Owner"],
  },
  {
    to: "/messages?new=1",
    label: "New Phone Message",
    icon: Phone,
    comp: "COMP-081",
    testid: "quick-new-phone-message",
    roles: ["Administrator", "Office Staff", "Owner"],
  },
];


/**
 * Five business workspaces. Ordering matches PO directive:
 *   🧭 Operations · ⛽ Fuel · 👥 People · 💬 Communications · ⚙ Settings
 *
 * Each `component` row = { to, label, icon, comp, roles, testid, ...meta }.
 *  - `comp`   : COMP-XXX id from the Component Registry.
 *  - `roles`  : temporary compat mapping until Sprint 5's
 *               permission-constants migration lands.
 *  - `accent` : subtle workspace tint token (from tailwind.config).
 *  - `meta`   : optional flags (primary, progressBadge, etc.) preserved
 *               from the legacy sidebar so behaviour doesn't regress.
 */
export const WORKSPACE_REGISTRY = [
  {
    id: "WS-01",
    label: "Operations",
    icon: Gauge,
    accent: "amber",
    primary: true,
    testid: "ws-operations",
    permission: "operations.view",
    defaultExpanded: true,
    components: [
      { to: "/ops",           label: "Operations Command Center", icon: Compass,       comp: "COMP-001", testid: "nav-ops",           roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/start-the-day", label: "Start the Day",             icon: SunHorizon,    comp: "COMP-002", testid: "nav-start-the-day", roles: ["Administrator", "Owner", "Office Staff"], progressBadge: true },
      { to: "/brief",         label: "Morning Brief",             icon: Sun,           comp: "COMP-003", testid: "nav-brief",         roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/fleet",         label: "Fleet",                     icon: MapPin,        comp: "COMP-020", testid: "nav-fleet",         roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/todays-orders", label: "Orders",                    icon: ClipboardText, comp: "COMP-010", testid: "nav-todays-orders", roles: ["Administrator", "Owner", "Office Staff", "Driver"] },
    ],
  },
  {
    id: "WS-02",
    label: "Fuel",
    icon: GasPump,
    accent: "red",
    testid: "ws-fuel",
    permission: "fuel.view",
    defaultExpanded: false,
    components: [
      { to: "/products",            label: "Products",            icon: Package,        comp: "COMP-040", testid: "nav-products",            roles: ["Administrator"] },
      { to: "/pricing",             label: "Pricing",             icon: CurrencyDollar, comp: "COMP-036", testid: "nav-pricing",             roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/price-notifications", label: "Daily Pricing",       icon: FileText,       comp: "COMP-030", testid: "nav-price-notifications", roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/purchasing",          label: "Purchasing",          icon: ShoppingCart,   comp: "COMP-090", testid: "nav-purchasing",          roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/receiving",           label: "Receiving",           icon: Package,        comp: "COMP-050", testid: "nav-receiving",           roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/fifo",                label: "FIFO Inventory",      icon: Stack,          comp: "COMP-070", testid: "nav-fifo",                roles: ["Administrator", "Owner", "Office Staff"] },
    ],
  },
  {
    id: "WS-03",
    label: "People",
    icon: UsersThree,
    accent: "blue",
    testid: "ws-people",
    permission: "people.view",
    defaultExpanded: false,
    components: [
      { to: "/customers", label: "Customers", icon: Users,         comp: "COMP-060", testid: "nav-customers", roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/drivers",   label: "Drivers",   icon: Truck,         comp: "COMP-061", testid: "nav-drivers",   roles: ["Administrator", "Owner", "Office Staff"] },
      { to: "/workforce", label: "Workforce", icon: CalendarCheck, comp: "COMP-111", testid: "nav-workforce", roles: ["Administrator", "Owner"] },
    ],
  },
  {
    id: "WS-04",
    label: "Communications",
    icon: ChatCircleDots,
    accent: "purple",
    testid: "ws-communications",
    permission: "communications.view",
    defaultExpanded: false,
    components: [
      { to: "/messages",            label: "Inbox",               icon: Phone,    comp: "COMP-080", testid: "nav-messages",            roles: ["Administrator", "Owner", "Office Staff"] },
    ],
  },
  {
    id: "WS-05",
    label: "Settings",
    icon: GearSix,
    accent: "slate",
    testid: "ws-settings",
    permission: "admin.view",
    defaultExpanded: false,
    components: [
      { to: "/administration",   label: "Administration",           icon: Gear,           comp: "COMP-110", testid: "nav-administration", roles: ["Administrator"] },
      { to: "/admin/thresholds", label: "Threshold Administration", icon: Sliders,        comp: "COMP-112", testid: "nav-thresholds",     roles: ["Administrator", "Owner"] },
      { to: "/settings/email-audit", label: "Email Audit",          icon: EnvelopeSimple, comp: "COMP-114", testid: "nav-email-audit",    roles: ["Administrator", "Owner"] },
      { to: "/history",              label: "History Replay",        icon: ClipboardText,  comp: "COMP-160", testid: "nav-history",        roles: ["Administrator", "Owner", "Auditor"] },
    ],
  },
];


/**
 * Navigation Builder — filter workspaces + components by the current
 * user's role. Returns an array of workspaces, each with its
 * `visibleComponents` list.
 *
 * NB: This does NOT enforce authorization — it decides what to
 * *render*. Every endpoint still validates permission independently
 * (ARC-012). If a user reaches a page they shouldn't see via a
 * bookmark or shared link, the backend still refuses the request.
 */
export function buildNavigation(role) {
  return WORKSPACE_REGISTRY
    .map((ws) => {
      const visibleComponents = ws.components.filter(
        (c) => !role || !c.roles || c.roles.includes(role)
      );
      return { ...ws, visibleComponents };
    })
    .filter((ws) => ws.visibleComponents.length > 0);
}


/**
 * Quick-actions builder — mirrors `buildNavigation` for the top
 * QUICK ACTIONS section. Same role-filter contract.
 */
export function buildQuickActions(role) {
  return QUICK_ACTIONS.filter(
    (a) => !role || !a.roles || a.roles.includes(role)
  );
}
